// src/stock/prets/ArticlesPretListe.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Button, Spinner, Modal, Form,
    Row, Col, Pagination, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn"><i className="ri-close-line fs-5"></i></button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   PAGINATION
   ============================================================ */
const TablePagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <Pagination className="pagination-charte mb-0">
            <Pagination.Prev onClick={() => onPageChange(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                    <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                    </Pagination.Item>
                );
            })}
            <Pagination.Next onClick={() => onPageChange(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
    );
};

const INITIAL_FORM = { reference: '', designation: '', categorie: '', etat: 'disponible', valeur_estimee: 0 };

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ArticlesPretListe() {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editArticle, setEditArticle] = useState(null);
    const [form, setForm] = useState(INITIAL_FORM);

    // États de chargement
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [formError, setFormError] = useState('');

    // Recherche & filtres
    const [filterText, setFilterText] = useState('');
    const [etatFilter, setEtatFilter] = useState('tous');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Tri
    const [sortField, setSortField] = useState('designation');
    const [sortDirection, setSortDirection] = useState('asc');

    // Toast
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => { fetchArticles(); }, []);
    useEffect(() => { setCurrentPage(1); }, [filterText, etatFilter, itemsPerPage]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchArticles = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/prets.php?action=articles`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
            const result = await res.json();
            setArticles(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       FILTRAGE / TRI / PAGINATION
       ============================================================ */
    const filteredAndSorted = useMemo(() => {
        const search = filterText.trim().toLowerCase();
        let filtered = articles.filter(a => {
            const matchText = !search ||
                (a.reference || '').toLowerCase().includes(search) ||
                (a.designation || '').toLowerCase().includes(search) ||
                (a.categorie || '').toLowerCase().includes(search);
            const matchEtat = etatFilter === 'tous' || a.etat === etatFilter;
            return matchText && matchEtat;
        });

        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            if (sortField === 'valeur_estimee') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });

        return filtered;
    }, [articles, filterText, etatFilter, sortField, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    const resetFilters = () => {
        setFilterText('');
        setEtatFilter('tous');
        setCurrentPage(1);
    };

    /* ============================================================
       STATS DYNAMIQUES
       ============================================================ */
    const dynamicStats = useMemo(() => {
        const total = filteredAndSorted.length;
        const disponibles = filteredAndSorted.filter(a => a.etat === 'disponible').length;
        const enPret = filteredAndSorted.filter(a => a.etat === 'en_pret').length;
        const valeurTotal = filteredAndSorted.reduce((sum, a) => sum + (parseFloat(a.valeur_estimee) || 0), 0);
        return { total, disponibles, enPret, valeurTotal };
    }, [filteredAndSorted]);

    /* ============================================================
       HELPERS
       ============================================================ */
    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortField === field && (
                    <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const getEtatBadge = (etat) => {
        const map = {
            disponible:   { bg: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-check-line',       label: 'Disponible' },
            en_pret:      { bg: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: 'ri-hand-coin-line',  label: 'En prêt' },
            maintenance:  { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', icon: 'ri-tools-line',      label: 'Maintenance' },
            hors_service: { bg: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-close-line',      label: 'Hors service' }
        };
        const cfg = map[etat] || map.disponible;
        return (
            <span className="status-badge-charte" style={{ background: cfg.bg }}>
                <i className={cfg.icon}></i> {cfg.label}
            </span>
        );
    };

    const openCreate = () => {
        setEditArticle(null);
        setForm(INITIAL_FORM);
        setFormError('');
        setShowModal(true);
    };

    const openEdit = (article) => {
        setEditArticle(article.id_article_pret);
        setForm({
            reference: article.reference || '',
            designation: article.designation || '',
            categorie: article.categorie || '',
            etat: article.etat || 'disponible',
            valeur_estimee: article.valeur_estimee || 0
        });
        setFormError('');
        setShowModal(true);
    };

    /* ============================================================
       ENREGISTRER
       ============================================================ */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!form.designation.trim()) {
            setFormError("La désignation est obligatoire");
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const method = editArticle ? 'PUT' : 'POST';
            const url = editArticle
                ? `${API_BASE_URL}/prets.php?id=${editArticle}`
                : `${API_BASE_URL}/prets.php`;
            const body = editArticle ? form : { action: 'create_article', ...form };

            const res = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            const result = await res.json();

            if (result.success) {
                const wasEdit = !!editArticle;
                setShowModal(false);
                setEditArticle(null);
                setForm(INITIAL_FORM);
                await fetchArticles();
                showToast('success',
                    wasEdit ? 'Article modifié' : 'Article créé',
                    `"${form.designation}" a été ${wasEdit ? 'mis à jour' : 'créé'} avec succès.`);
            } else {
                setFormError(result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            setFormError(err.message || "Erreur de connexion");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       SUPPRIMER
       ============================================================ */
    const supprimer = async (article) => {
        if (!window.confirm(`Désactiver l'article "${article.designation}" ?`)) return;

        setDeletingId(article.id_article_pret);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/prets.php?id=${article.id_article_pret}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();

            if (result.success) {
                await fetchArticles();
                showToast('success', 'Article supprimé', `"${article.designation}" a été désactivé.`);
            } else {
                showToast('error', 'Erreur', result.message || "Impossible de supprimer");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setDeletingId(null);
        }
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !articles.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des articles...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-home-line"></i></div>
                            <div>
                                <h5 className="header-title">Articles prêtables</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {dynamicStats.total}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-check-line"></i> Disponibles {dynamicStats.disponibles}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-hand-coin-line"></i> En prêt {dynamicStats.enPret}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchArticles} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/prets')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-primary" onClick={openCreate}>
                                <i className="ri-add-line"></i> Nouvel article
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-home-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total articles</span>
                                    <h3 className="kpi-value-charte">{dynamicStats.total}</h3>
                                    <span className="kpi-unit-charte">catalogue</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-check-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Disponibles</span>
                                    <h3 className="kpi-value-charte">{dynamicStats.disponibles}</h3>
                                    <span className="kpi-unit-charte">prêts à partir</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-hand-coin-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">En prêt</span>
                                    <h3 className="kpi-value-charte">{dynamicStats.enPret}</h3>
                                    <span className="kpi-unit-charte">actuellement</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Valeur totale</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1.1rem' }}>
                                        {formatCurrency(dynamicStats.valeurTotal)}
                                    </h3>
                                    <span className="kpi-unit-charte">cumul estimé</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher référence, désignation, catégorie..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={etatFilter}
                                        onChange={(e) => setEtatFilter(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Tous les états</option>
                                        <option value="disponible">Disponible</option>
                                        <option value="en_pret">En prêt</option>
                                        <option value="maintenance">Maintenance</option>
                                        <option value="hors_service">Hors service</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <Form.Select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="filter-select-charte"
                                    >
                                        <option value={5}>5 / page</option>
                                        <option value={10}>10 / page</option>
                                        <option value={25}>25 / page</option>
                                        <option value={50}>50 / page</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2" className="text-end">
                                    {(filterText || etatFilter !== 'tous') ? (
                                        <Button onClick={resetFilters} className="btn-modern-light-outline">
                                            <i className="ri-close-line"></i> Reset
                                        </Button>
                                    ) : (
                                        <span className="count-badge-charte">
                                            <i className="ri-stack-line"></i> {filteredAndSorted.length}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex align-items-center gap-2">
                                <i className="ri-box-3-line text-white fs-5"></i>
                                <span className="fw-semibold text-white">Liste des articles</span>
                                <span className="badge-header-light">{filteredAndSorted.length}</span>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <SortableHeader field="reference" className="ps-4">Référence</SortableHeader>
                                            <SortableHeader field="designation">Désignation</SortableHeader>
                                            <SortableHeader field="categorie">Catégorie</SortableHeader>
                                            <SortableHeader field="valeur_estimee" className="text-end">Valeur</SortableHeader>
                                            <SortableHeader field="etat" className="text-center">État</SortableHeader>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map(a => {
                                            const isDeleting = deletingId === a.id_article_pret;
                                            return (
                                                <tr key={a.id_article_pret} style={{ opacity: isDeleting ? 0.5 : 1 }}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-barcode-line me-1"></i>
                                                            {a.reference}
                                                        </span>
                                                    </td>
                                                    <td style={{ minWidth: '200px' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="article-icon-charte">
                                                                <i className="ri-hand-coin-line"></i>
                                                            </div>
                                                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                {a.designation}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {a.categorie ? (
                                                            <span className="category-badge-charte">
                                                                <i className="ri-price-tag-3-line me-1"></i>
                                                                {a.categorie}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                                            {formatCurrency(a.valeur_estimee)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">{getEtatBadge(a.etat)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => openEdit(a)}
                                                                    disabled={isDeleting}
                                                                >
                                                                    <i className="ri-pencil-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Désactiver</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => supprimer(a)}
                                                                    disabled={isDeleting}
                                                                    style={{ '--btn-color': COLORS.danger }}
                                                                >
                                                                    {isDeleting ? (
                                                                        <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
                                                                    ) : (
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    )}
                                                                </button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="6">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte"><i className="ri-box-3-line"></i></div>
                                                        <h6 className="fw-semibold mb-1">
                                                            {filterText || etatFilter !== 'tous'
                                                                ? 'Aucun résultat'
                                                                : 'Aucun article'}
                                                        </h6>
                                                        <p className="text-muted small mb-3">
                                                            {filterText || etatFilter !== 'tous'
                                                                ? 'Aucun article ne correspond à vos critères.'
                                                                : 'Commencez par créer votre premier article prêtable.'}
                                                        </p>
                                                        {!(filterText || etatFilter !== 'tous') && (
                                                            <Button className="btn-modern-primary" onClick={openCreate}>
                                                                <i className="ri-add-line me-2"></i> Créer le premier article
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* ===== PAGINATION ===== */}
                            {totalPages > 1 && !loading && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSorted.length)}</strong> sur <strong>{filteredAndSorted.length}</strong> article(s)
                                    </div>
                                    <TablePagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL ===== */}
            <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered backdrop={saving ? 'static' : true}>
                <Modal.Header closeButton={!saving} className="card-header-gradient">
                    <Modal.Title className="text-white">
                        <i className={`${editArticle ? 'ri-pencil-line' : 'ri-add-line'} me-2`}></i>
                        {editArticle ? 'Modifier' : 'Nouvel'} article
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {formError && (
                            <div className="alert alert-danger py-2 px-3 small mb-3">{formError}</div>
                        )}
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Référence</Form.Label>
                                    <Form.Control
                                        value={form.reference}
                                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                        placeholder="Auto si vide"
                                        disabled={saving || !!editArticle}
                                    />
                                    {editArticle && (
                                        <Form.Text className="text-muted small">
                                            La référence ne peut pas être modifiée
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">
                                        Désignation <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        value={form.designation}
                                        onChange={(e) => setForm({ ...form, designation: e.target.value })}
                                        disabled={saving}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Catégorie</Form.Label>
                                    <Form.Control
                                        value={form.categorie || ''}
                                        onChange={(e) => setForm({ ...form, categorie: e.target.value })}
                                        disabled={saving}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">État</Form.Label>
                                    <Form.Select
                                        value={form.etat}
                                        onChange={(e) => setForm({ ...form, etat: e.target.value })}
                                        disabled={saving}
                                    >
                                        <option value="disponible">Disponible</option>
                                        <option value="en_pret">En prêt</option>
                                        <option value="maintenance">Maintenance</option>
                                        <option value="hors_service">Hors service</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Valeur estimée (FCFA)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.valeur_estimee}
                                        onChange={(e) => setForm({ ...form, valeur_estimee: parseFloat(e.target.value) || 0 })}
                                        disabled={saving}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            className="btn-modern-primary"
                            disabled={saving}
                            style={{ minWidth: '160px' }}
                        >
                            {saving ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <i className="ri-save-line me-2"></i>
                                    {editArticle ? 'Mettre à jour' : 'Enregistrer'}
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid var(--secondary); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, var(--primary), var(--secondary)); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25); }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern { background: #f1f4f9; color: #475569; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte { background: white; border-radius: 12px; padding: 1.2rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid; transition: all 0.25s ease; height: 100%; }
                .kpi-charte:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value-charte { font-size: 1.35rem; font-weight: 700; margin: 0.3rem 0; color: #1e293b; }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte { background: linear-gradient(135deg, var(--primary-dark), var(--primary)); border-radius: 12px; border: none; box-shadow: 0 8px 24px rgba(41,90,167,0.15); }
                .search-box-charte { position: relative; }
                .search-icon-charte { position: absolute; top: 50%; left: 1rem; transform: translateY(-50%); color: var(--primary); font-size: 1rem; z-index: 2; pointer-events: none; }
                .search-input-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem 0.6rem 2.75rem !important; font-size: 0.9rem; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem !important; font-size: 0.85rem; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-modern-light-outline { background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); color: white; border-radius: 50px; padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 500; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-light-outline:hover { background: rgba(255,255,255,0.25); color: white; }
                .count-badge-charte { background: rgba(255,255,255,0.95); color: var(--primary); padding: 0.6rem 1.25rem; border-radius: 50px; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%); border-top-left-radius: 0px !important; border-top-right-radius: 0px !important; padding: 1rem 1.5rem; border: none; }
                .badge-header-light { background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); border-radius: 50px; padding: 0.25rem 0.75rem; font-size: 0.75rem; font-weight: 600; }

                /* ===== TABLE ===== */
                .table-charte thead th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 0.9rem 0.75rem; }
                .table-charte tbody td { padding: 0.9rem 0.75rem; vertical-align: middle; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }

                .article-icon-charte { width: 38px; height: 38px; border-radius: 10px; background: ${COLORS.primary}15; color: var(--primary); display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
                .reference-badge-charte { display: inline-flex; align-items: center; background: #f1f5f9; padding: 0.25rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; color: #475569; }
                .category-badge-charte { display: inline-flex; align-items: center; background: ${COLORS.primary}15; color: var(--primary); padding: 0.25rem 0.65rem; border-radius: 50px; font-weight: 600; font-size: 0.7rem; }
                .status-badge-charte { display: inline-flex; align-items: center; gap: 4px; color: white; padding: 0.3rem 0.75rem; border-radius: 50px; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }

                /* ===== ACTION BTN ===== */
                .action-btn-charte { width: 36px; height: 36px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: white; border: 1.5px solid #e2e8f0; color: #64748b; cursor: pointer; transition: all 0.2s ease; }
                .action-btn-charte:hover:not(:disabled) { border-color: var(--btn-color, ${COLORS.primary}); color: var(--btn-color, ${COLORS.primary}); transform: translateY(-1px); }
                .action-btn-charte:disabled { opacity: 0.5; cursor: not-allowed; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte { width: 72px; height: 72px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8; }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); flex-wrap: wrap; gap: 1rem; }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link { border-radius: 10px; border: 1.5px solid #e2e8f0; color: #1e293b; padding: 0.5rem 0.9rem; font-weight: 500; font-size: 0.85rem; transition: all 0.2s; }
                .pagination-charte .page-item.active .page-link { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border-color: var(--primary); color: white; box-shadow: 0 4px 12px rgba(41,90,167,0.3); }
                .pagination-charte .page-item .page-link:hover { background: #f1f5f9; border-color: var(--secondary); color: var(--primary); transform: translateY(-1px); }

                /* ===== BOUTONS ===== */
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; font-weight: 500; color: #4b5563; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:hover { border-color: var(--secondary); color: var(--secondary); background: #f0fbff; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(33,172,205,0.15); }
                .btn-modern-primary { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; font-size: 0.85rem; transition: all 0.2s ease; box-shadow: 0 4px 12px rgba(41,90,167,0.3); display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white; }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; opacity: 0.8; }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }

                /* ===== FORM ===== */
                .form-label-modern { font-weight: 600; color: #334155; font-size: 0.85rem; }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}